import { apiClient } from './client';
import type { ApiResponse } from './types';

export type UsageSource = 'actual' | 'estimated' | 'unpriced';

export interface UsageListParams {
  from: string;
  to: string;
  page: number;
  pageSize: number;
  sort?: string;
  search?: string;
  agentKey?: string;
  providerKey?: string;
  model?: string;
}

export interface TokenAggregate {
  sum: string | null;
  reportedCount: number;
  eventCount: number;
}

export interface TenantUsageListItem {
  tenantId: number;
  tenantCode: string;
  displayName: string;
  balance: string;
  isUnlimited: boolean;
  totalCreditsUsed: string;
  periodCreditsUsed: string;
  unpricedCount: number;
}

export interface PagedResult<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

export interface AdminUsageItem {
  id: string;
  date: string;
  agentKey: string;
  agentName: string;
  creditsUsed: string | null;
  source: UsageSource;
}

export interface TenantUsageSummary extends PagedResult<AdminUsageItem> {
  agentOptions: { agentKey: string; agentName: string }[];
  tenantId: number;
  balance: string;
  isUnlimited: boolean;
  totalCreditsUsed: string;
  todayCreditsUsed: string;
  periodCreditsUsed: string;
  unpricedCount: number;
}

export interface UsageBreakdownItem {
  providerKey: string;
  model: string;
  providerLabel: string;
  cacheMissTokens: TokenAggregate;
  inputTotalTokens: TokenAggregate;
  cacheReadTokens: TokenAggregate;
  outputTokens: TokenAggregate;
  creditsUsed: string;
  unpricedCount: number;
}

export interface ModelUsageItem extends UsageBreakdownItem {
  tenantCount: number;
}

export interface AdminUsageDailyPoint {
  date: string;
  creditsUsed: string;
}

export interface PublicCreditsAccount {
  tenantId: number;
  balance: string | null;
  isUnlimited: boolean;
  totalCreditsUsed: string;
  retainedBalance?: string;
}

export interface RateSnapshot {
  model: string;
  billingRevision?: number;
  tokensPerCredit: number;
  revision: number;
  isFallback: boolean;
  asrTokensPerSecond: string | null;
  visionBytesPerToken: string | null;
}

export const usageQueryKeys = {
  root: ['usage'] as const,
  tenants: (params: UsageListParams) => ['usage', 'tenants', params] as const,
  models: (params: UsageListParams) => ['usage', 'models', params] as const,
  tenant: (id: number, params: UsageListParams) => ['usage', 'tenant', id, params] as const,
  breakdown: (id: number, params: UsageListParams) => ['usage', 'breakdown', id, params] as const,
  daily: (id: number, params: UsageListParams) => ['usage', 'daily', id, params] as const,
  rates: (model?: string) => ['usage', 'rates', model] as const,
};

function unwrap<T>(r: { data: ApiResponse<T> }): T {
  return r.data.data!;
}

export async function listTenantUsage(params: UsageListParams): Promise<PagedResult<TenantUsageListItem>> {
  const r = await apiClient.get<ApiResponse<PagedResult<TenantUsageListItem>>>('/usage/tenants', { params });
  return unwrap(r);
}

export async function getTenantUsage(id: number, params: UsageListParams): Promise<TenantUsageSummary> {
  const r = await apiClient.get<ApiResponse<TenantUsageSummary>>(`/tenants/${id}/usage`, { params });
  return unwrap(r);
}

export async function getTenantBreakdown(id: number, params: UsageListParams): Promise<PagedResult<UsageBreakdownItem>> {
  const r = await apiClient.get<ApiResponse<PagedResult<UsageBreakdownItem>>>(`/tenants/${id}/usage/breakdown`, { params });
  return unwrap(r);
}

export async function getTenantDaily(id: number, params: UsageListParams): Promise<{ items: AdminUsageDailyPoint[] }> {
  const r = await apiClient.get<ApiResponse<{ items: AdminUsageDailyPoint[] }>>(`/tenants/${id}/usage/daily`, { params });
  return unwrap(r);
}

export async function listModelUsage(params: UsageListParams): Promise<PagedResult<ModelUsageItem>> {
  const r = await apiClient.get<ApiResponse<PagedResult<ModelUsageItem>>>('/usage/models', { params });
  return unwrap(r);
}

export async function rechargeCredits(
  tenantId: number,
  body: { amount: string; reason: string },
  idempotencyKey: string,
): Promise<PublicCreditsAccount> {
  const r = await apiClient.post<ApiResponse<PublicCreditsAccount>>(
    `/tenants/${tenantId}/credits/recharge`,
    body,
    { headers: { 'Idempotency-Key': idempotencyKey } },
  );
  return unwrap(r);
}

export async function setUnlimited(
  tenantId: number,
  body: { enabled: boolean; reason: string },
): Promise<PublicCreditsAccount> {
  const r = await apiClient.put<ApiResponse<PublicCreditsAccount>>(`/tenants/${tenantId}/credits/unlimited`, body);
  return unwrap(r);
}

export async function listRates(model?: string): Promise<{ items: RateSnapshot[] }> {
  const r = await apiClient.get<ApiResponse<{ items: RateSnapshot[] }>>('/usage/rates', {
    params: model ? { model } : undefined,
  });
  return unwrap(r);
}

export async function updateRate(body: {
  model: string;
  expectedRevision: number;
  asrTokensPerSecond: string | null;
  visionBytesPerToken: string | null;
}): Promise<RateSnapshot> {
  const r = await apiClient.put<ApiResponse<RateSnapshot>>('/usage/rates', body);
  return unwrap(r);
}
